// The location switcher (DLG-009; legacy `AppSidebarLocation` + `AppSidebarLocationWrapper`): the sidebar button shows
// the location's colour and city and turns its swap icon into an X while open; the panel has "Search location", a
// "LOCATIONS (n)" heading counting the other locations that match, the current location's card on top, then the others
// by name, each with its initial, name and address. Search matches name, city, state or zip. Choosing another location
// shows "Updating Location. Please wait..." then "Location updated successfully" and opens the same page there (a
// drawer's detail opens that location's Drawers list).
import { useState } from 'react';
import { useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { Popover } from 'radix-ui';
import { ArrowLeftRight, CircleAlert, Search, X } from 'lucide-react';
import { backofficePalette } from '@radish/ui-tokens';
import { useActiveLocation } from '../app/location.tsx';
import { markSwitcherPick } from '../session/session.ts';
import { useLocationQuery } from '../data/api.tsx';

export interface SwitcherLocation {
  readonly locationId: string;
  readonly name: string;
  readonly slug: string;
  readonly address: { readonly line1: string; readonly city: string; readonly state: string; readonly zip: string };
  readonly color: string;
}

/** Legacy's `bg-${default_label_color}`: the back office's shade of the location's colour (B1065). */
const colorOf = (c: string): string => (backofficePalette as Readonly<Record<string, string>>)[c] ?? backofficePalette.blue;
/** "line1, city, state zip" with blank parts left out (legacy joins them anyway and shows ", ," without an address, B173). */
export const addressOf = (l: SwitcherLocation): string => {
  const { line1, city, state, zip } = l.address;
  const stateZip = [state.trim(), zip.trim()].filter((v) => v !== '').join(' ');
  return [line1.trim(), city.trim(), stateZip].filter((v) => v !== '').join(', ');
};

/** The other locations legacy lists for a search: not the current one, matching name, city, state or zip, by name. */
export function otherLocations(all: readonly SwitcherLocation[], currentId: string, term: string): SwitcherLocation[] {
  const t = term.trim().toLowerCase();
  const matches = (l: SwitcherLocation) => t === '' || [l.name, l.address.city, l.address.state, l.address.zip].some((v) => v.toLowerCase().includes(t));
  const key = (l: SwitcherLocation) => l.name.toUpperCase();
  return all.filter((l) => l.locationId !== currentId && matches(l)).sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0));
}

/** The page at another location: the same path under its slug; a drawer's detail becomes the Drawers list. */
export function pathAt(pathname: string, slug: string): string {
  const rest = pathname.replace(/^\/l\/[^/]+/, '').replace(/^(\/reports\/audit\/drawer)\/[^/]+$/, '$1');
  return `/l/${slug}${rest}`;
}

function Card({ location, selected, onPick }: { readonly location: SwitcherLocation; readonly selected: boolean; readonly onPick?: () => void }) {
  const body = (
    <>
      <span aria-hidden className="inline-flex size-10 shrink-0 items-center justify-center rounded-full text-[16px] text-white" style={{ backgroundColor: colorOf(location.color) }}>
        {location.name.charAt(0).toLocaleUpperCase()}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-medium text-text-1">{location.name}</span>
        {addressOf(location) === '' ? null : <span className="block text-[13px] text-text-2" data-testid="location-address">{addressOf(location)}</span>}
      </span>
    </>
  );
  const cls = `mb-2 flex w-full items-center gap-3 rounded-[0.5em] border px-2 py-4 text-left ${selected ? 'border-primary' : 'border-line hover:border-primary'}`;
  return onPick === undefined
    ? <div className={cls} data-testid="location-current">{body}</div>
    : <button type="button" className={`${cls} cursor-pointer`} onClick={onPick}>{body}</button>;
}

export function LocationSwitcher() {
  const location = useActiveLocation();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // `?pick=1`: the tab lost its location at a token refresh (A192, ops plan 2026-10-23-tab-scoped-refresh), so the
  // layout brought it here with a toast and the picker opens at once; closing it drops the parameter.
  const picking = String((useSearch({ strict: false }) as { pick?: unknown }).pick ?? '') === '1';
  const [open, setOpen] = useState(picking);
  const [term, setTerm] = useState('');
  const [progress, setProgress] = useState<string | null>(null);
  const listed = useLocationQuery(location.locationId, 'tenancy.locations.list', {}, (client) => client.queries.tenancy.locations.list({}));
  // Only the locations this session can open, with their Location Info address and colour.
  const slugs = new Map(location.all.map((l) => [l.locationId, l.slug]));
  const all: SwitcherLocation[] = (listed.data?.locations ?? []).flatMap((l) => {
    const slug = slugs.get(l.locationId);
    return slug === undefined ? [] : [{ locationId: l.locationId, name: l.name, slug, address: l.address, color: l.color }];
  });
  const current = all.find((l) => l.locationId === location.locationId);
  const others = otherLocations(all, location.locationId, term);
  // Legacy shows the city on the button (and spun forever without one, B159): the name stands in for a missing city.
  const label = current === undefined ? location.name : current.address.city.trim() || current.name;

  const pick = (target: SwitcherLocation) => {
    setOpen(false);
    setTerm('');
    if (target.locationId === location.locationId) return;
    setProgress('Updating Location. Please wait...');
    setTimeout(() => setProgress('Location updated successfully'), 150);
    setTimeout(() => {
      setProgress(null);
      // Only this pick moves a merchant's default location, as legacy's setDefaultLocation does (B1727).
      markSwitcherPick(target.locationId);
      void navigate({ to: pathAt(pathname, target.slug) as never });
    }, 450);
  };

  return (
    <>
      <Popover.Root open={open} onOpenChange={(next) => {
        setOpen(next);
        setTerm('');
        if (!next && picking) void navigate({ to: pathname as never, replace: true });
      }}>
        <Popover.Trigger
          aria-label={`Location: ${location.name}. Switch location`}
          data-testid="location-switcher"
          // Legacy AppSidebarLocation (measured live 2026-10-13): a 36 px button with 8 px corners, the dot 7 px in, the name
          // 4 px after it (q-ml-xs) in regular weight.
          className="flex h-9 w-full items-center gap-1 rounded-[8px] bg-white/10 pl-[7px] pr-3 text-left text-[14px] font-normal text-white"
        >
          <span aria-hidden className="size-[1.4em] shrink-0 rounded-full" style={{ backgroundColor: colorOf(current?.color ?? 'blue') }} />
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {open ? <X size={18} aria-hidden /> : <ArrowLeftRight size={18} aria-hidden />}
        </Popover.Trigger>
        <Popover.Portal>
          {/* Legacy's q-menu (measured live 2026-10-14): right under the button with no gap, 230 px in from its left edge
              (`:offset="[-230, 0]"`), so it opens just past the sidebar (`sticky="always"`: the default limit would hold it at the button's edge); 4 px corners and Quasar's menu shadow, no border. */}
          <Popover.Content
            align="start" side="bottom" sideOffset={0} alignOffset={230} sticky="always" data-align-offset="230" data-side-offset="0" data-testid="location-switcher-panel"
            className="z-[3000] w-[400px] max-w-[calc(100vw-32px)] rounded-[4px] bg-menu p-4 shadow-[0_1px_5px_rgba(0,0,0,0.2),0_2px_2px_rgba(0,0,0,0.14),0_3px_1px_-2px_rgba(0,0,0,0.12)] dark:shadow-none"
          >
            <label className="sr-only" htmlFor="location-search">Search location</label>
            <div className="relative mb-4">
              <Search size={18} aria-hidden className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3" />
              <input
                id="location-search"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Search location"
                className="h-10 w-full rounded-q border border-line bg-field pl-9 pr-3 text-[14px] text-text-1 outline-none focus:border-primary focus:ring-1 focus:ring-primary focus:ring-inset"
              />
            </div>
            <p className="mb-4 text-[14px] font-medium uppercase text-text-1">{`Locations (${others.length})`}</p>
            {current === undefined ? null : <div className="mb-4"><Card location={current} selected /></div>}
            {others.length === 0
              ? (
                <p role="alert" className="flex items-center gap-3 rounded-q bg-negative px-4 py-3 text-[13px] text-white">
                  <CircleAlert size={20} aria-hidden />No Locations found!!
                </p>
              )
              : (
                <ul data-testid="location-list" className="max-h-[300px] overflow-y-auto">
                  {others.map((l) => <li key={l.locationId}><Card location={l} selected={false} onPick={() => pick(l)} /></li>)}
                </ul>
              )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {progress === null ? null : (
        <div role="status" data-testid="location-progress" className="fixed inset-0 z-[4000] flex items-center justify-center bg-black/40">
          <div className="rounded-q bg-gray-50 px-6 pb-4 pt-6 text-[14px] text-text-1 shadow-lg">{progress}</div>
        </div>
      )}
    </>
  );
}
